import * as React from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import type { TaskSummary } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { openTasksOf } from '@/lib/my-tasks';
import { formatDuration } from '@/lib/duration';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** Returns a timestamp that advances while active, so the counter moves without re-fetching the session. */
function useNow(active: boolean): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

/**
 * The work-log pages carry these same controls in full, so the header widget
 * stands down there rather than offering a second set of buttons.
 */
export function WorkLogHeaderControl(): React.ReactElement | null {
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  if (currentPath === '/work-log' || currentPath.startsWith('/work-log/')) return null;
  return <WorkLogHeaderControlInner />;
}

function WorkLogHeaderControlInner(): React.ReactElement | null {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [noTask, setNoTask] = React.useState(false);

  const currentQuery = useQuery({
    queryKey: ['work-log', 'current'],
    queryFn: () => api.getCurrentWorkSession(),
    retry: false,
  });

  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.getMyTasks(),
    enabled: pickerOpen,
    retry: false,
  });

  const session = currentQuery.data ?? null;
  const now = useNow(session !== null && !session.onBreak);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['work-log'] });
  };

  const clockInMutation = useMutation({
    mutationFn: () => api.clockIn({ taskIds: noTask ? [] : selected, noTask }),
    onSuccess: async () => {
      setPickerOpen(false);
      setSelected([]);
      setNoTask(false);
      await refresh();
    },
  });
  const breakStartMutation = useMutation({
    mutationFn: () => api.startWorkBreak(),
    onSuccess: refresh,
  });
  const breakEndMutation = useMutation({
    mutationFn: () => api.endWorkBreak(),
    onSuccess: refresh,
  });

  const openTasks: TaskSummary[] = React.useMemo(
    () => openTasksOf(tasksQuery.data),
    [tasksQuery.data],
  );

  const liveMinutes = React.useMemo(() => {
    if (!session) return 0;
    const gross = Math.max(0, Math.floor((now - new Date(session.clockInAt).getTime()) / 60_000));
    return Math.max(0, gross - session.breakMinutes);
  }, [session, now]);

  const busy =
    clockInMutation.isPending || breakStartMutation.isPending || breakEndMutation.isPending;

  if (currentQuery.isPending || currentQuery.isError) return null;

  if (session === null) {
    return (
      <>
        <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
          <Clock className="size-4" />
          Clock in
        </Button>

        <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clock in</DialogTitle>
              <DialogDescription>
                Pick the tasks you are starting on, or choose “No task”. Time started with no task
                lands in your queue to map later.
              </DialogDescription>
            </DialogHeader>

            {clockInMutation.error ? (
              <div className="rounded-lg border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-4 py-3 text-sm">
                {toMessage(clockInMutation.error)}
              </div>
            ) : null}

            <div className="max-h-72 space-y-2 overflow-y-auto">
              {tasksQuery.isPending ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading your tasks…</p>
              ) : openTasks.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  No tasks assigned to you right now.
                </p>
              ) : (
                openTasks.map((t) => (
                  <label
                    key={t.id}
                    className={
                      'flex items-center gap-3 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm ' +
                      (noTask
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer hover:bg-[hsl(var(--accent))]')
                    }
                  >
                    <input
                      type="checkbox"
                      disabled={noTask}
                      checked={!noTask && selected.includes(t.id)}
                      onChange={(e) =>
                        setSelected((prev) =>
                          e.target.checked ? [...prev, t.id] : prev.filter((id) => id !== t.id),
                        )
                      }
                    />
                    <span className="flex-1">{t.title}</span>
                  </label>
                ))
              )}
            </div>

            <label
              className={
                'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ' +
                (noTask
                  ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/[0.06]'
                  : 'border-dashed border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))]')
              }
            >
              <input
                type="checkbox"
                checked={noTask}
                onChange={(e) => {
                  setNoTask(e.target.checked);
                  if (e.target.checked) setSelected([]);
                }}
              />
              <span className="flex-1">
                No task
                <span className="block text-xs text-[hsl(var(--muted-foreground))]">
                  Start without a task — this time goes to your queue to map later.
                </span>
              </span>
            </label>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPickerOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={clockInMutation.isPending || (!noTask && selected.length === 0)}
                onClick={() => clockInMutation.mutate()}
              >
                {clockInMutation.isPending ? 'Clocking in…' : 'Clock in'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className="flex items-center gap-2 rounded-full bg-[hsl(var(--muted))] px-3 py-1 text-xs tabular-nums"
        title={session.onBreak ? 'On break' : 'Clocked in'}
      >
        <span
          className={
            session.onBreak
              ? 'size-2 rounded-full bg-[hsl(var(--muted-foreground))]'
              : 'size-2 rounded-full bg-[hsl(var(--primary))]'
          }
        />
        {session.onBreak ? 'On break' : formatDuration(liveMinutes)}
      </span>

      {session.onBreak ? (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => breakEndMutation.mutate()}
        >
          End break
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => breakStartMutation.mutate()}
        >
          Start break
        </Button>
      )}

      <Button
        size="sm"
        disabled={busy || session.onBreak}
        title={session.onBreak ? 'End your break before clocking out.' : undefined}
        onClick={() => void navigate({ to: '/work-log/clock-out' })}
      >
        Clock out
      </Button>
    </div>
  );
}
